import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { createRef, Fragment, useCallback, useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import styled from 'styled-components';
import { GYPSUM, OBSIDIAN } from 'HubStyleTokens/colors';
import { TEMPLATE_SECTION_DIVIDER_BORDER_COLOR } from 'HubStyleTokens/theme';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import { rgba } from 'UIComponents/core/Color';
import H1 from 'UIComponents/elements/headings/H1';
import UIBadge from 'UIComponents/badge/UIBadge';
import UIButton from 'UIComponents/button/UIButton';
import UIButtonWrapper from 'UIComponents/layout/UIButtonWrapper';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIGrid from 'UIComponents/grid/UIGrid';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import UIBreadcrumbs from 'UIComponents/nav/UIBreadcrumbs';
import UISection from 'UIComponents/section/UISection';
import UITabs from 'UIComponents/nav/UITabs';
import UITab from 'UIComponents/nav/UITab';
import * as ScreenSizes from 'marketplace-ui-common/layout/constants/ScreenSizes';
import { getAssetEventSelectorId } from '../../utils/hsAnalytics';
import { getLocationFromQuery } from '../../common/utils/assetQuery';
import { getModulesStorefrontRoute, getTemplatesHomePageRoute, getThemesStorefrontRoute } from '../../utils/urls';
import { ModuleQuery } from '../../modules/records/ModuleQuery';
import { MAIN_SECTION_MAX_WIDTH } from 'marketplace-ui-common/layout/constants';
import { ThemeQuery } from '../../themes/records/ThemeQuery';
import { useHeaderTabs } from 'marketplace-ui-common/hooks/useHeaderTabs';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import { useScrollEventListener } from 'marketplace-ui-common/hooks/useScrollEventListener';
import { useWindowDimensionsContext } from 'marketplace-ui-common/hooks/useWindowDimensionsContext';
import Rating from 'marketplace-ui-reviews/components/Rating';
import MediaQuery from 'marketplace-ui-common/layout/components/MediaQuery';
import AssetDemoButton from './AssetDemoButton';
import AssetDownloadButton from './AssetDownloadButton';
import AssetSetupGuideButton from './AssetSetupGuideButton';
import AssetShareButton from './AssetShareButton';
const STICKY_OVERHANG_HEIGHT = 14;
const TABS_CLASS = 'asset-details__header__tabs';
const TABS_OUTER_WRAPPER_CLASS = 'asset-details__header__tabs__outer';
const TABS_INNER_WRAPPER_CLASS = 'asset-details__header__tabs__inner';
const TABS_FLEX_WRAPPER_CLASS = 'asset-details__header__tabs__flex';
const TABS_HEIGHT = 49;

const NoopLink = ({
  children
}) => children;

const StyledTabsContainer = styled.div.attrs(props => ({
  className: props.sticky ? 'is--sticky' : ""
})).withConfig({
  displayName: "AssetDetailsHeader__StyledTabsContainer",
  componentId: "sc-1gx49tv-0"
})([".", "{height:", "px;}&&.is--sticky{height:", "px;.", "{position:fixed;top:0;left:0;right:0;z-index:2;background-color:", ";border-bottom:1px solid ", ";}.", "{position:relative;}.", "{position:relative;margin-left:auto;margin-right:auto;width:100%;max-width:", "px;}}"], TABS_CLASS, TABS_HEIGHT, TABS_HEIGHT, TABS_OUTER_WRAPPER_CLASS, GYPSUM, TEMPLATE_SECTION_DIVIDER_BORDER_COLOR, TABS_INNER_WRAPPER_CLASS, TABS_FLEX_WRAPPER_CLASS, MAIN_SECTION_MAX_WIDTH);
const StyledStickyOverhang = styled.div.withConfig({
  displayName: "AssetDetailsHeader__StyledStickyOverhang",
  componentId: "sc-1gx49tv-1"
})(["position:absolute;bottom:-", "px;left:0;height:", "px;width:100%;background-image:linear-gradient(to top,", ",", ");opacity:0.1;pointer-events:none;"], STICKY_OVERHANG_HEIGHT, STICKY_OVERHANG_HEIGHT, rgba(OBSIDIAN, 0), OBSIDIAN);
export default function AssetDetailsHeader({
  assetDetails,
  assetScopes,
  exampleUrl,
  isEmbedded,
  isPreview,
  name,
  offeringType,
  onTrackInteraction,
  onTrackPageview,
  providerName,
  reviewSummary
}) {
  const {
    isMobileView
  } = useResponsiveProps({
    isMobileView: {
      [ScreenSizes.XS]: true,
      [ScreenSizes.SM]: false
    }
  });
  const {
    setupUrl
  } = assetDetails;
  const headingSectionClasses = classNames(isEmbedded && 'p-top-10', isMobileView && 'm-bottom-0');
  const tabsWrapperElementRef = /*#__PURE__*/createRef();
  const [sticky, setSticky] = useState(false);
  const {
    height: windowHeight
  } = useWindowDimensionsContext();
  const scrollOffset = TABS_HEIGHT + windowHeight / 2;
  const {
    calculateActiveTab,
    scrollToTabSection,
    activeTab
  } = useHeaderTabs(['overview', 'features', 'contents', 'support', 'reviews'], scrollOffset);
  useScrollEventListener(calculateActiveTab);
  const scrollAnimationFrame = useCallback(() => {
    if (tabsWrapperElementRef.current) {
      setSticky(tabsWrapperElementRef.current.getBoundingClientRect().top <= 0);
    }
  }, [tabsWrapperElementRef]);
  const trackInteraction = useCallback((action, eventProperties = {}) => {
    onTrackInteraction(action, Object.assign({}, eventProperties, {
      section: sticky ? 'sticky header' : 'header'
    }));
  }, [onTrackInteraction, sticky]);
  const trackBreadcrumbLinkClick = useCallback(value => {
    trackInteraction('click breadcrumb link', {
      value
    });
  }, [trackInteraction]);
  const trackTabClick = useCallback(value => {
    trackInteraction('click tab navigation', {
      value
    });
  }, [trackInteraction]);
  const providerQuery = useMemo(() => {
    return offeringType === 'MODULE' ? new ModuleQuery({
      providers: [providerName]
    }) : new ThemeQuery({
      providers: [providerName]
    });
  }, [offeringType, providerName]);
  const location = useLocation();
  const providerAssetLocation = useMemo(() => {
    return getLocationFromQuery(providerQuery, location);
  }, [location, providerQuery]);
  useEffect(() => {
    const scrollHandler = () => {
      window.requestAnimationFrame(scrollAnimationFrame);
    };

    window.addEventListener('scroll', scrollHandler);
    return () => {
      window.removeEventListener('scroll', scrollHandler);
    };
  }, [scrollAnimationFrame]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [!isEmbedded && /*#__PURE__*/_jsx(UISection, {
      children: offeringType === 'MODULE' ? /*#__PURE__*/_jsxs(UIBreadcrumbs, {
        flush: true,
        children: [/*#__PURE__*/_jsx(UIRouterLink, {
          disabled: isPreview,
          onClick: () => trackBreadcrumbLinkClick('templates storefront home'),
          to: getTemplatesHomePageRoute(),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.modules.details.breadcrumbs.root"
          })
        }), /*#__PURE__*/_jsx(UIRouterLink, {
          disabled: isPreview,
          onClick: () => trackBreadcrumbLinkClick('modules'),
          to: getModulesStorefrontRoute(),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.modules.details.breadcrumbs.modules"
          })
        }), /*#__PURE__*/_jsx("span", {
          children: name
        })]
      }) : /*#__PURE__*/_jsxs(UIBreadcrumbs, {
        flush: true,
        children: [/*#__PURE__*/_jsx(UIRouterLink, {
          disabled: isPreview,
          onClick: () => trackBreadcrumbLinkClick('templates storefront home'),
          to: getTemplatesHomePageRoute(),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.themes.details.breadcrumbs.root"
          })
        }), /*#__PURE__*/_jsx(UIRouterLink, {
          disabled: isPreview,
          onClick: () => trackBreadcrumbLinkClick('themes'),
          to: getThemesStorefrontRoute('all'),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.themes.details.breadcrumbs.themes"
          })
        }), /*#__PURE__*/_jsx("span", {
          children: name
        })]
      })
    }), /*#__PURE__*/_jsxs(UISection, {
      className: headingSectionClasses,
      children: [/*#__PURE__*/_jsx(H1, {
        "aria-level": 1,
        children: name
      }), /*#__PURE__*/_jsxs(UIFlex, {
        align: "baseline",
        wrap: "wrap",
        children: [/*#__PURE__*/_jsx(UIBadge, {
          use: "koala",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `marketplace-assets-ui.details.offeringType.${offeringType}`
          })
        }), /*#__PURE__*/_jsx("span", {
          className: "display-inline-block m-x-3",
          children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "marketplace-assets-ui.details.header.providerName_jsx",
            options: {
              providerName,
              linkProps: {
                'data-hs-analytics-id': getAssetEventSelectorId('HEADER-PROVIDER-WEBSITE', offeringType),
                to: providerAssetLocation,
                onClick: () => trackInteraction('click provider link')
              }
            },
            elements: {
              Link: isEmbedded ? NoopLink : UIRouterLink
            }
          })
        }), reviewSummary && reviewSummary.reviewCount ? /*#__PURE__*/_jsx(Rating, {
          iconSize: "xxs",
          label: /*#__PURE__*/_jsx(UIButton, {
            onClick: () => {
              scrollToTabSection('reviews');
              trackInteraction('click review count');
            },
            use: "link",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-assets-ui.details.header.reviewCount",
              options: {
                count: reviewSummary.reviewCount
              }
            })
          }),
          rating: reviewSummary.overallRating
        }) : null]
      })]
    }), /*#__PURE__*/_jsx(MediaQuery, {
      maxScreenSize: "md",
      children: /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx(UISection, {
          className: "text-center p-top-4",
          children: /*#__PURE__*/_jsxs(UIFlex, {
            children: [/*#__PURE__*/_jsx(AssetDownloadButton, {
              assetDetails: assetDetails,
              assetScopes: assetScopes,
              className: isMobileView ? 'm-right-3' : '',
              offeringType: offeringType,
              section: "header",
              style: {
                flexBasis: '50%'
              }
            }), /*#__PURE__*/_jsx(AssetDemoButton, {
              className: "m-top-0",
              exampleUrl: exampleUrl,
              modalPrimaryButton: /*#__PURE__*/_jsx(AssetDownloadButton, {
                assetDetails: assetDetails,
                assetScopes: assetScopes,
                offeringType: offeringType
              }),
              name: name,
              onTrackInteraction: onTrackInteraction,
              onTrackPageview: onTrackPageview,
              providerName: providerName,
              section: "header",
              style: {
                flexBasis: '50%'
              }
            })]
          })
        }), /*#__PURE__*/_jsxs(UISection, {
          className: "text-center p-y-0",
          children: [/*#__PURE__*/_jsx(AssetSetupGuideButton, {
            className: "m-right-3",
            offeringType: offeringType,
            setupUrl: setupUrl,
            style: {
              width: 'auto'
            }
          }), /*#__PURE__*/_jsx(AssetShareButton, {
            section: "header",
            style: {
              width: 'auto'
            },
            use: "link"
          })]
        })]
      })
    }), /*#__PURE__*/_jsx(StyledTabsContainer, {
      ref: tabsWrapperElementRef,
      sticky: sticky,
      children: /*#__PURE__*/_jsx("div", {
        className: TABS_OUTER_WRAPPER_CLASS,
        children: /*#__PURE__*/_jsxs("div", {
          className: TABS_INNER_WRAPPER_CLASS,
          children: [/*#__PURE__*/_jsxs(UIFlex, {
            align: "center",
            className: TABS_FLEX_WRAPPER_CLASS,
            justify: "between",
            children: [/*#__PURE__*/_jsx(MediaQuery, {
              minScreenSize: "md",
              children: /*#__PURE__*/_jsx(UIGrid, {
                style: {
                  width: sticky ? '75%' : '100%'
                },
                children: /*#__PURE__*/_jsx(UIGridItem, {
                  size: {
                    xs: 12,
                    md: sticky ? 12 : 8
                  },
                  children: /*#__PURE__*/_jsxs(UITabs, {
                    bordered: false,
                    className: TABS_CLASS,
                    defaultSelected: "overview",
                    onSelectedChange: ({
                      target: {
                        value
                      }
                    }) => {
                      scrollToTabSection(value);
                    },
                    selected: activeTab,
                    children: [/*#__PURE__*/_jsx(UITab, {
                      onClick: () => trackTabClick('overview'),
                      tabId: "overview",
                      title: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "marketplace-assets-ui.details.header.tabs.overview"
                      })
                    }), /*#__PURE__*/_jsx(UITab, {
                      onClick: () => trackTabClick('features'),
                      tabId: "features",
                      title: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "marketplace-assets-ui.details.header.tabs.features"
                      })
                    }), offeringType === 'THEME' && /*#__PURE__*/_jsx(UITab, {
                      onClick: () => trackTabClick('contents'),
                      tabId: "contents",
                      title: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "marketplace-assets-ui.details.header.tabs.contents"
                      })
                    }), /*#__PURE__*/_jsx(UITab, {
                      onClick: () => trackTabClick('support'),
                      tabId: "support",
                      title: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "marketplace-assets-ui.details.header.tabs.support"
                      })
                    }), /*#__PURE__*/_jsx(UITab, {
                      onClick: () => trackTabClick('reviews'),
                      tabId: "reviews",
                      title: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "marketplace-assets-ui.details.header.tabs.reviews"
                      })
                    })]
                  })
                })
              })
            }), sticky && /*#__PURE__*/_jsx(MediaQuery, {
              minScreenSize: "md",
              children: /*#__PURE__*/_jsxs(UIButtonWrapper, {
                className: "p-right-3",
                children: [/*#__PURE__*/_jsx(AssetDemoButton, {
                  exampleUrl: exampleUrl,
                  modalPrimaryButton: /*#__PURE__*/_jsx(AssetDownloadButton, {
                    assetDetails: assetDetails,
                    assetScopes: assetScopes,
                    offeringType: offeringType
                  }),
                  name: name,
                  onTrackInteraction: onTrackInteraction,
                  onTrackPageview: onTrackPageview,
                  providerName: providerName,
                  section: "sticky-header",
                  size: "small"
                }), /*#__PURE__*/_jsx(AssetDownloadButton, {
                  assetDetails: assetDetails,
                  assetScopes: assetScopes,
                  offeringType: offeringType,
                  size: "small"
                })]
              })
            })]
          }), sticky && /*#__PURE__*/_jsx(StyledStickyOverhang, {})]
        })
      })
    })]
  });
}